import { useNavigate } from 'react-router-dom';
import { formatPln } from '../catalog/money';
import { PAY_METHODS, type ExpectedPayment } from './types';

const dayLabel = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric' });
const lateLabel = (days: number) => `zaległa ${days} ${days === 1 ? 'dzień' : 'dni'}`;

/**
 * F45 "Oczekiwane płatności": open receivables due by the month's end (late ones first, in red, with days late) and
 * the quotes of the month's events without receivables or invoices; the method as a chip. A row opens its event's
 * "Płatności" tab when the reader may see events.
 */
export function ExpectedPaymentsTable({ rows, canOpen }: { rows: ExpectedPayment[]; canOpen: boolean }) {
  const navigate = useNavigate();
  const open = (eventId: string) => navigate(`/wydarzenia?wydarzenie=${eventId}&zakladka=platnosci`);
  return (
    <section className="fin-section" aria-label="Oczekiwane płatności" data-feature-id="finance.expected_payments">
      <h3>Oczekiwane płatności</h3>
      {rows.length === 0 ? (
        <p className="muted">Nic nie czeka na zapłatę w tym miesiącu.</p>
      ) : (
        <div className="table-wrap">
          <table className="table fin-table" aria-label="Oczekiwane płatności">
            <thead>
              <tr>
                <th scope="col">Termin</th>
                <th scope="col">Klient</th>
                <th scope="col">Wydarzenie</th>
                <th scope="col">Za co</th>
                <th scope="col">Sposób</th>
                <th scope="col" className="num">Kwota</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const late = r.overdueDays > 0;
                const clickable = canOpen && r.eventId !== null;
                return (
                  <tr key={r.receivableId ?? `q-${r.eventId}`} className={clickable ? 'clickable' : undefined} onClick={clickable ? () => open(r.eventId!) : undefined}>
                    <td className={late ? 'neg' : r.kind === 'kosztorys' ? 'fin-forecast' : undefined}>
                      {r.dueDate ? dayLabel(r.dueDate) : 'bez terminu'}
                      {late && <div className="fin-late">{lateLabel(r.overdueDays)}</div>}
                    </td>
                    <td>{r.contractorName || '—'}</td>
                    <td>{r.eventName || '—'}</td>
                    <td className={r.kind === 'kosztorys' ? 'fin-forecast' : undefined}>{r.label}</td>
                    <td>
                      <span className={`chip${r.method === 'barter' ? ' accent' : ''}`}>{PAY_METHODS[r.method]}</span>
                    </td>
                    <td className={`num${late ? ' neg' : ''}`}>{formatPln(r.amountGrosze)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
